// Ported verbatim from legacy app.js `pqSections`.
// Sections marked `customRenderer` (employment, assets, liabilities, income,
// taxesExpenses) need bespoke React components — see TODOs in PQScreen.

import { SF_TEXT_LIMITS } from './fieldLimits.js';

export const pqSections = [
  {
    id: 'family',
    colorTheme: 'blue',
    title: 'Household / Family',
    fields: [
      { key: 'client1FirstName', label: 'First Name', width: 'medium', required: true },
      { key: 'client1LastName', label: 'Last Name', width: 'medium', required: true },
      { key: 'client1Nickname', label: 'Nickname', width: 'medium' },
      { key: 'client1DOB', label: 'Date of Birth', type: 'date', width: 'medium', dateRange: 'birthdate', newRow: true, emoney: true },
      { key: 'client1Age', label: 'Age', type: 'computed', width: 'field-2' },
    ],
    conditionalBlocks: [
      {
        id: 'spouse-block',
        toggleLabel: '+ Add Spouse / Partner',
        hideLabel: '- Remove Spouse',
        flag: 'hasSpouse',
        fields: [
          { key: 'client2FirstName', label: 'First Name', width: 'medium' },
          { key: 'client2LastName', label: 'Last Name', width: 'medium' },
          { key: 'client2Nickname', label: 'Nickname', width: 'medium' },
          { key: 'client2DOB', label: 'Date of Birth', type: 'date', width: 'medium', dateRange: 'birthdate', newRow: true, emoney: true },
          { key: 'client2Age', label: 'Age', type: 'computed', width: 'field-2' },
          { key: 'yearsMarried', label: 'Years Married', type: 'number', width: 'field', min: 0, max: 99 },
        ],
      },
    ],
    afterConditional: [
      {
        id: 'children-block',
        toggleLabel: '+ Add Children / Grandchildren',
        // "Hide", not "Remove": unlike the spouse block, collapsing this
        // section keeps the entered rows (no onToggleOff wipe) — the label
        // should promise what the button actually does.
        hideLabel: '- Hide Children / Grandchildren',
        flag: 'hasChildren',
        tables: [
          { key: 'children', title: 'Children', columns: [
            { key: 'name', label: 'Name' },
            { key: 'age', label: 'Age', type: 'number', min: 0, max: 120 },
          ] },
          // Names are optional here: advisors usually take only the number
          // of grandchildren (Jake, 2026-10-06), which the count below holds.
          { key: 'grandchildren', title: 'Grandchildren (names optional)', columns: [
            { key: 'name', label: 'Name' },
            { key: 'age', label: 'Age', type: 'number', min: 0, max: 120 },
          ] },
        ],
        summaryFields: [
          // Editable fallback counts. A user can type a total directly (useful
          // when they don't itemize every child), but as soon as the table has
          // rows with data (name or age), the count is overwritten by the
          // populated-row count — see useAutoCalcFields. The manual value is
          // kept only while the table has no populated rows. (This intentionally
          // reopens the input closed under PQ-11; the snap-to-count is now the
          // requested behavior, not a bug.)
          { key: 'numChildren', label: '# of Children', type: 'number', min: 0, width: 'field', integer: true },
          { key: 'numGrandchildren', label: '# of Grandchildren', type: 'number', min: 0, width: 'field', integer: true },
        ],
      },
    ],
    footerFields: [
      { key: 'additionalFamilyInfo', label: 'Additional Family Information', type: 'textarea' },
    ],
  },
  {
    id: 'contact',
    colorTheme: 'teal',
    title: 'Contact / Residence',
    fields: [
      { key: 'address1', label: 'Street Address', width: 'wide' },
      { key: 'address2', label: 'Address Line 2', width: 'wide' },
      { key: 'city', label: 'City', width: 'medium' },
      { key: 'state', label: 'State', width: 'field' },
      { key: 'zip', label: 'Zip Code', width: 'field' },
      { key: 'homePhone', label: 'Home Phone', type: 'tel', width: 'medium' },
      { key: 'client1Cell', label: 'Client Cell Phone', type: 'tel', width: 'medium' },
      { key: 'client1Email', label: 'Client Email', type: 'email', width: 'wide' },
      { key: 'client2Cell', label: 'Spouse Cell Phone', type: 'tel', width: 'medium', showIf: 'hasSpouse' },
      { key: 'client2Email', label: 'Spouse Email', type: 'email', width: 'wide', showIf: 'hasSpouse' },
      { key: 'referredBy', label: 'Lead Source/Referred By', width: 'medium' },
    ],
  },
  {
    id: 'employment',
    colorTheme: 'violet',
    title: 'Employment',
    customRenderer: 'renderEmploymentSection',
  },
  {
    id: 'relationships',
    colorTheme: 'emerald',
    title: 'Current Professional Relationships',
    tables: [
      {
        key: 'professionals',
        ariaTitle: 'Professional Relationships',
        reorderable: true,
        columns: [
          { key: 'role', label: 'Role' },
          { key: 'name', label: 'Name' },
          { key: 'firm', label: 'Firm' },
          { key: 'city', label: 'City' },
          { key: 'state', label: 'State' },
        ],
        // No starter rows (Jake, 2026-07-23). Pre-seeding Financial Advisor /
        // Attorney / Accountant meant a client with none of them still showed
        // three empty line items on the client-facing Classic view. The advisor
        // now adds only the professionals the client actually has. (The
        // _seeded / SEEDED_PRO_ROLES handling stays for records saved earlier.)
        starterRows: [],
      },
    ],
  },
  { id: 'assets', colorTheme: 'indigo', title: 'Assets', customRenderer: 'renderAssetsSection' },
  { id: 'liabilities', colorTheme: 'rose', title: 'Liabilities', customRenderer: 'renderLiabilitiesSection' },
  {
    id: 'insurance',
    colorTheme: 'sky',
    title: 'Insurance',
    // Lets an advisor record "asked, client has none" per coverage type without
    // inventing an empty policy row. Grouped by what advisors actually ask
    // about rather than one marker per picklist value, so "Life" covers all
    // three life products and "P&C" covers auto and homeowners.
    noneMarkers: {
      key: 'noneOf',
      label: 'Asked, client has none:',
      tableKey: 'policies',
      typeColumn: 'type',
      options: [
        { value: 'Life', covers: ['Term Life', 'Whole Life', 'Universal Life'] },
        { value: 'LTC', covers: ['LTC'] },
        { value: 'Disability', covers: ['Disability'] },
        { value: 'Umbrella', covers: ['Umbrella/Liability'] },
        { value: 'P&C', covers: ['Auto (P&C)', 'Homeowners (P&C)'] },
      ],
    },
    tables: [
      {
        key: 'policies',
        ariaTitle: 'Insurance Policies',
        reorderable: true,
        // detail: true = demoted to the per-row panel when squeezed (R2).
        // Primary keeps the policy's identity + money: Company, Type,
        // Benefit, Premium, Beneficiary.
        columns: [
          { key: 'company', label: 'Company', colClass: 'col-wide' },
          // Property & casualty (auto / homeowners) added per Jake 2026-07-23:
          // advisors record the client's P&C carrier during the meeting and had
          // nowhere to put it. NOTE: needs the matching Salesforce picklist
          // values from Srivani before these two persist.
          { key: 'type', label: 'Type', type: 'select', options: ['Term Life', 'Whole Life', 'Universal Life', 'LTC', 'Disability', 'Umbrella/Liability', 'Auto (P&C)', 'Homeowners (P&C)'] },
          { key: 'benefit', label: 'Death/Daily Benefit', type: 'currency' },
          { key: 'insured', label: 'Insured', detail: true, maxLength: SF_TEXT_LIMITS['PQ_Insurance__c.Insured__c'] },
          { key: 'owner', label: 'Owner', detail: true, maxLength: SF_TEXT_LIMITS['PQ_Insurance__c.Owner__c'] },
          { key: 'policyDate', label: 'Policy Date', type: 'date', dateRange: 'historical', detail: true },
          // The term printed on the policy, which eMoney counts from the policy
          // date. Without it every term policy went across as 20 years (P41).
          // Only a term policy has one, so the box is greyed out on the rest.
          {
            key: 'termYears', label: 'Term (Years)', type: 'number', integer: true, min: 1, max: 50, detail: true,
            appliesWhen: row => row?.type === 'Term Life', appliesHint: 'Only term life policies have a term.',
          },
          { key: 'annualPremium', label: 'Annual Premium', type: 'currency' },
          { key: 'cashValue', label: 'Cash Value', type: 'currency', detail: true },
          { key: 'beneficiary', label: 'Beneficiary', colClass: 'col-wide' },
        ],
      },
    ],
  },
  { id: 'income', colorTheme: 'green', title: 'Income', customRenderer: 'renderIncomeSection' },
  { id: 'taxesExpenses', colorTheme: 'orange', title: 'Taxes & Expenses', customRenderer: 'renderTaxesExpensesSection' },
  // Estate Plan sits at the end of the intake flow (MD demo feedback 2026-07-07):
  // it's lighter-touch reference info, so it follows the financial core.
  {
    id: 'goals',
    colorTheme: 'amber',
    title: 'Estate Plan',
    fields: [
      // "None" records that the question was ASKED and the client has none of
      // these, which a blank field cannot say (Jake plus a second advisor, via
      // Jimmy 2026-08-11). exclusiveOption stops it being combined with the
      // others, so the record can never claim "None and a Trust". It
      // round-trips for free: estatePlan is already sent as a comma-joined
      // string and split back on load.
      { key: 'estatePlan', label: 'Estate Plan', type: 'multiselect', options: ['Trust', 'Will', 'FPOA', 'MPOA', 'None'], exclusiveOption: 'None', width: 'medium' },
      { key: 'yearsEstablished', label: 'Year Established / Updated', width: 'medium', maxLength: SF_TEXT_LIMITS['Financial_Profile__c.Estate_Plan_Years_Established__c'] },
    ],
  },
];
